import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "../redux/store";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";
import { AppointmentState } from "../types";
import { sendToWindowDataLayer } from "../utils/dom";
import { ApptType } from "../types/booking-config.interface";

interface StayOrDropSelectorProps {
  appointment: AppointmentState;
  stayOrDropOff: ApptType | undefined;
  setStayOrDropOff: (value: ApptType) => void;
}

export const StayOrDropSelector: React.FC<StayOrDropSelectorProps> = ({
  appointment,
  stayOrDropOff,
  setStayOrDropOff,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const accentColor = appointment?.shopData?.color || "#2b73b9";
  const lastAutoSelectedChoiceRef = useRef<ApptType | null>(null);
  const appointmentWithSingleOptionFlags = appointment as AppointmentState & {
    stayOnly?: boolean;
    waitOnly?: boolean;
  };

  const isChoiceDisabled = useCallback(
    (choice: ApptType) => {
      const isDropOffOnly = Boolean(appointment?.dropOffOnly);
      const isStayOnly = Boolean(
        appointmentWithSingleOptionFlags?.stayOnly ||
          appointmentWithSingleOptionFlags?.waitOnly
      );

      if (choice === "wait") return isDropOffOnly;
      if (choice === "drop") return isStayOnly;
      return false;
    },
    [
      appointment?.dropOffOnly,
      appointmentWithSingleOptionFlags?.stayOnly,
      appointmentWithSingleOptionFlags?.waitOnly,
    ]
  );

  const handleChoice = useCallback((choice: ApptType) => {
    if (isChoiceDisabled(choice)) return;

    dispatch(
      updateAppointment({
        updates: { apptType: choice },
      })
    );
    setStayOrDropOff(choice);

    if (choice === "wait") {
      sendToWindowDataLayer("dc-service-wait_or_drop", "Stay");
    } else if (choice === "drop") {
      sendToWindowDataLayer("dc-service-wait_or_drop", "Drop Off");
    }
  }, [dispatch, isChoiceDisabled, setStayOrDropOff]);

  const choices: { type: ApptType; label: string; icon: string }[] = [
    { type: "wait", label: "Stay", icon: "fa-solid fa-clock" },
    { type: "drop", label: "Drop Off", icon: "fa-solid fa-key" },
  ];

  const availableChoices = choices.filter(({ type }) => !isChoiceDisabled(type));

  useEffect(() => {
    if (availableChoices.length !== 1) return;
    const onlyAvailableChoice = availableChoices[0].type;

    // Guard against repeated auto-selection dispatch loops.
    if (lastAutoSelectedChoiceRef.current === onlyAvailableChoice) return;
    lastAutoSelectedChoiceRef.current = onlyAvailableChoice;

    if (stayOrDropOff !== onlyAvailableChoice) {
      setStayOrDropOff(onlyAvailableChoice);
    }

    if (appointment?.apptType !== onlyAvailableChoice) {
      dispatch(
        updateAppointment({
          updates: { apptType: onlyAvailableChoice },
        })
      );
    }
  }, [
    appointment?.apptType,
    availableChoices,
    dispatch,
    setStayOrDropOff,
    stayOrDropOff,
  ]);

  return (
    <div
      className="rounded-2xl bg-white p-5 font-roboto"
      style={{
        border: `2px solid ${accentColor}33`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <h3
        className="text-base font-bold text-center mb-4"
        style={{ color: "#1f2937" }}
      >
        Will you be dropping your vehicle off?
      </h3>

      <div className="grid grid-cols-2 gap-3">
        {choices.map(({ type, label, icon }) => {
          const isSelected = type === stayOrDropOff;
          const isDisabled = isChoiceDisabled(type);
          const displayLabel =
            type === "wait" && appointment?.dropOffOnly
              ? appointment?.shopData?.messages?.dropOffOnlyMessage ||
                "This is a drop off only service"
              : label;

          return (
            <button
              key={type}
              type="button"
              data-dc-service={`dc-service-${type}`}
              className={`flex flex-col items-center justify-center gap-2 rounded-xl py-4 px-3 transition-all font-roboto ${
                isDisabled
                  ? "cursor-not-allowed opacity-40"
                  : "cursor-pointer hover:scale-[1.02]"
              }`}
              style={{
                backgroundColor: isSelected ? accentColor : "#ffffff",
                border: `2px solid ${accentColor}`,
                color: isSelected ? "#ffffff" : accentColor,
              }}
              onClick={() => handleChoice(type)}
              disabled={!!isDisabled}
            >
              <i
                className={icon}
                aria-hidden="true"
                style={{ fontSize: "24px", lineHeight: 1 }}
              />
              <span className="text-sm font-semibold">{displayLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
